import React, { useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useForm, useWatch, Controller, FormProvider } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useStoreState } from "ezStore";

import { zodResolver } from "@hookform/resolvers/zod";

import { Button, Card, FormGroup, PageHeader, Textarea } from "@cofano/cck";

import { Grid2 as Grid, Box, Tabs, Tab, CircularProgress } from "@mui/material";
import Info from "@mui/icons-material/Info";

import useTabs from "components/hooks/useTabs";
import TimePicker from "components/cck/TimePicker";
import Helmet from "components/helmet/Helmet";
import { request } from "components/utils/Request2";

import { Language, Fields, hasPluginPermission, hasOptionalFieldPermission } from "imports/Utils";

import RelationDetailsForm from "./components/form/RelationDetailsForm";
import RelationGeneralForm from "./components/form/RelationGeneralForm";
import RelationContactsForm from "./components/form/RelationContactsForm";
import RelationShipsForm, { RelationShipsTable } from "./components/form/RelationShipsForm";
import RelationLocationsForm from "./components/form/RelationLocationsForm";
import RelationProductsForm from "./components/form/RelationProductsForm";
import RelationLinkedCustomersForm from "./components/form/RelationLinkedCustomersForm";

import ExternalMasterdataAlert, { ExternalMasterdataWarningIcon, MasterdataEntity } from "components/masterdata/ExternalMasterdataAlert";

import { relationSchema, RelationSchemaInput, RelationSchemaOutput } from "view/relations/utils/relation.schema";
import { RelationTabs, showContactsTab } from "view/relations/utils/RelationUtils";

import { Day } from "types/enums";

import { LocationUserType, RelationType } from "__generated__/enums";
import { GetRelationDocument } from "__generated__/view/relations/graphql/relation.queries.generated";
import { SaveRelationDocument } from "__generated__/view/relations/graphql/relation.mutations.generated";

const RelationCreateEdit = () => {

	const navigate = useNavigate();
	const location = useLocation();
	const { id } = useParams();

	const session = useStoreState(store => store.session);

	const [ saveRelation, { loading } ] = useMutation(SaveRelationDocument);

	const stateTab = location?.state?.tab;
	const type = location.state?.type;

	const form = useForm<RelationSchemaInput, unknown, RelationSchemaOutput>({
		resolver: zodResolver(relationSchema),
		defaultValues: {
			id: null,
			customerNumber: null,
			externalId: "",
			name: "",
			email: "",
			language: null,
			abbreviation: "",
			scac: "",
			ean: "",
			productGroup: null,
			externalCode: "",
			externalCustomerNumber: "",
			organisation: "",
			telephone: "",
			volumePerYear: null,
			transportFrequency: "",

			isCustomer: type ? (type === RelationType.Customer) : true,
			isSupplier: (type === RelationType.Supplier),
			isOperator: (type === RelationType.Operator),
			isShipManager: (type === RelationType.ShipManager),
			isSkipper: (type === RelationType.Skipper),
			isShippingCompany: (type === RelationType.ShippingCompany),
			isApplicationRelation: (type === RelationType.ApplicationRelation),
			isLocationRelation: (type === RelationType.LocationRelation),
			isAgent: (type === RelationType.Agent),
			isConsignor: (type === RelationType.Consignor),
			isContainerOperator: (type === RelationType.ContainerOperator),
			isBroker: (type === RelationType.Broker),
			isSurveyor: (type === RelationType.Surveyor),

			maxCargoDeviationForLetterOfProtest: null,
			maxCargoDeviationForCallKWKW: null,
			roundVatEachLine: false,
			termsAndConditionsPurchase: "",
			termsAndConditionsSale: "",
			logo: null,

			address: {
				town: "",
				street: "",
				zipCode: "",
				name1: "",
				name2: "",
				country: "NL",
			},

			accountHolder: "",
			iban: "",
			bic: "",
			vatReference: "",
			cocReference: "",

			workingHours: {
				id: null,
				mondayFrom:	null,
				mondayTo: null,
				tuesdayFrom: null,
				tuesdayTo: null,
				wednesdayFrom: null,
				wednesdayTo: null,
				thursdayFrom: null,
				thursdayTo: null,
				fridayFrom: null,
				fridayTo: null,
				saturdayFrom: null,
				saturdayTo: null,
				sundayFrom: null,
				sundayTo: null,
			},

			contacts: [],
			managedShips: null,
			operatorShips: null,
			skipperShip: null,
			locations: [],
			products: null,
			linkedRelations: null,
			invoiceGroupLinks: [],
			remarks: "",
		},
	});

	const { selectedTab, selectTab } = useTabs({
		defaultSelected: stateTab || RelationTabs.General,
	});

	const {
		isCustomer,
		isSupplier,
		isShipManager,
		isSkipper,
		isOperator,
		isLocationRelation: isLocationManager,
		isBroker,
		isSurveyor,
		skipperShip,
	} = useWatch({
		control: form.control,
	});

	const save = (data: RelationSchemaOutput) => {
		saveRelation({
			variables: {
				input: {
					...data,
					logo: undefined,
				},
			},
		}).then((response) => {
			if (data.logo) {
				request({
					url: `relation/logo/${response.data?.saveRelation?.id}`,
					method: "POST",
					body: {
						logo: data.logo,
					},
				}).then(() => {
					navigate(`/view/relations/${response.data?.saveRelation?.id}/details`, { state: { tab: selectedTab } });
				});
			} else {
				navigate(`/view/relations/${response.data?.saveRelation?.id}/details`, { state: { tab: selectedTab } });
			}
		});
	};

	const [ getRelation, { loading: getRelationLoading } ] = useLazyQuery(GetRelationDocument, {
		onCompleted: response => {
			form.reset({
				id: response.relation?.id ?? null,
				customerNumber: response.relation?.customerNumber ? Number(response.relation.customerNumber) : null,
				externalId: response.relation?.externalId ?? "",
				name: response.relation?.name ?? "",
				email: response.relation?.email ?? "",
				language: response.relation?.language ? {
					id: response.relation.language.id,
					description: response.relation.language.description,
					code: response.relation.language.code,
				} : null,
				abbreviation: response.relation?.abbreviation ?? "",
				scac: response.relation?.scac ?? "",
				ean: response.relation?.ean ?? "",
				productGroup: response.relation?.productGroup ?? null,
				externalCode: response.relation?.externalCode ?? "",
				externalCustomerNumber: response.relation?.externalCustomerNumber ?? "",
				organisation: response.relation?.organisation ?? "",
				telephone: response.relation?.telephone ?? "",
				volumePerYear: response.relation?.volumePerYear ?? null,
				transportFrequency: response.relation?.transportFrequency ?? "",
				isCustomer: response.relation?.isCustomer ?? false,
				isSupplier: response.relation?.isSupplier ?? false,
				isOperator: response.relation?.isOperator ?? false,
				isShipManager: response.relation?.isShipManager ?? false,
				isSkipper: response.relation?.isSkipper ?? false,
				isShippingCompany: response.relation?.isShippingCompany ?? false,
				isApplicationRelation: response.relation?.isApplicationRelation ?? false,
				isLocationRelation: response.relation?.isLocationRelation ?? false,
				isAgent: response.relation?.isAgent ?? false,
				isConsignor: response.relation?.isConsignor ?? false,
				isContainerOperator: response.relation?.isContainerOperator ?? false,
				isBroker: response.relation?.isBroker ?? false,
				isSurveyor: response.relation?.isSurveyor ?? false,

				maxCargoDeviationForLetterOfProtest: response.relation?.maxCargoDeviationForLetterOfProtest ?? null,
				maxCargoDeviationForCallKWKW: response.relation?.maxCargoDeviationForCallKWKW ?? null,
				roundVatEachLine: response.relation?.roundVatEachLine ?? false,
				termsAndConditionsPurchase: response.relation?.termsAndConditionsPurchase ?? "",
				termsAndConditionsSale: response.relation?.termsAndConditionsSale ?? "",
				logo: response.relation?.logo ?? null,

				address: {
					town: response.relation?.address?.town ?? "",
					street: response.relation?.address?.street ?? "",
					zipCode: response.relation?.address?.zipCode ?? "",
					name1: response.relation?.address?.name1 ?? "",
					name2: response.relation?.address?.name2 ?? "",
					country: response.relation?.address?.country ?? "NL",
				},

				accountHolder: response.relation?.invoiceDetails?.accountName ?? "",
				iban: response.relation?.invoiceDetails?.iban ?? "",
				bic: response.relation?.invoiceDetails?.bic ?? "",
				vatReference: response.relation?.invoiceDetails?.vatReference ?? "",
				cocReference: response.relation?.invoiceDetails?.cocReference ?? "",

				workingHours: {
					id: response.relation?.workingHours?.id ?? null,
					mondayFrom: response.relation?.workingHours?.mondayFrom ?? null,
					mondayTo: response.relation?.workingHours?.mondayTo ?? null,
					tuesdayFrom: response.relation?.workingHours?.tuesdayFrom ?? null,
					tuesdayTo: response.relation?.workingHours?.tuesdayTo ?? null,
					wednesdayFrom: response.relation?.workingHours?.wednesdayFrom ?? null,
					wednesdayTo: response.relation?.workingHours?.wednesdayTo ?? null,
					thursdayFrom: response.relation?.workingHours?.thursdayFrom ?? null,
					thursdayTo: response.relation?.workingHours?.thursdayTo ?? null,
					fridayFrom: response.relation?.workingHours?.fridayFrom ?? null,
					fridayTo: response.relation?.workingHours?.fridayTo ?? null,
					saturdayFrom: response.relation?.workingHours?.saturdayFrom ?? null,
					saturdayTo: response.relation?.workingHours?.saturdayTo ?? null,
					sundayFrom: response.relation?.workingHours?.sundayFrom ?? null,
					sundayTo: response.relation?.workingHours?.sundayTo ?? null,
				},

				contacts: response.relation?.contacts?.map(c => ({
					id: c.id,
					type: c.type,
					contactRoleTypes: c.contactRoleTypes,
					name: c.name ?? "",
					insertion: c.insertion ?? "",
					surName: c.surName ?? "",
					email: c.email ?? "",
					telephone: c.telephone ?? "",
					defaultContact: c.defaultContact ?? false,
					visibleForShip: c.visibleForShip ?? false,
					communicateStatusChanges: c.communicateStatusChanges ?? false,
				})) ?? [],

				managedShips: response.relation?.managedShips ?? null,
				skipperShip: response.relation?.skipperShip ?? null,
				operatorShips: response.relation?.operatorShips ?? null,

				locations: response.relation?.locations?.map(l => ({
					id: l.id,
					location: l.location,
					type: l.type ?? LocationUserType.Owner,
				})) ?? [],

				products: response.relation?.products ?? null,

				linkedRelations: response.relation?.linkedRelations ?? null,

				invoiceGroupLinks: response.relation?.invoiceGroupLinks
					?.filter(l => l.invoiceGroupType.id != null)
					.map(l => ({
						invoiceGroupTypeId: l.invoiceGroupType.id!,
						linkedRelation: l.linkedRelation,
					})) ?? [],

				remarks: response.relation?.remarks ?? "",
			});
		},
	});

	const hasErrorInContactsTab = () => {
		return !!form.formState.errors?.contacts;
	};

	useEffect(() => {
		if (id) {
			getRelation({
				variables: {
					id: Number(id),
				},
			});
		}
	}, [ id ]);

	return (
		<FormProvider {...form}>
			<div>
				<form onSubmit={form.handleSubmit(save)}>
					<PageHeader>
						<PageHeader.Title>
							{Language("RELATION")}
							<Helmet title={intl.formatMessage({ id: "RELATION" })} />
						</PageHeader.Title>

						<PageHeader.Actions>
							<Button onClick={() => navigate(id ? `/view/relations/${id}/details` : "/view/relations", { state: { tab: selectedTab } })} variant="outlined">
								{Language("CANCEL")}
							</Button>

							<Button loading={loading} type="submit">
								{Language("SAVE")}
							</Button>

							<ExternalMasterdataWarningIcon entity={MasterdataEntity.Relation} />
						</PageHeader.Actions>
					</PageHeader>

					<ExternalMasterdataAlert entity={MasterdataEntity.Relation} externalId={form.watch("externalId")} />

					{getRelationLoading ? (
						<CircularProgress size={24} />
					) : (
						<Grid container spacing={3}>
							<Grid size={{ xs: 12, md: 5 }}>
								<Card title={Language("DETAILS")} icon={<Info />}>
									<RelationDetailsForm />
								</Card>
							</Grid>

							<Grid size={{ xs: 12, md: 7 }}>
								<Card disablePadding>
									<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
										<Tabs
											variant='scrollable'
											value={selectedTab}
											onChange={(_event, newValue) => selectTab(newValue)}>

											<Tab label={Language("GENERAL")} value={RelationTabs.General} />

											{hasOptionalFieldPermission(session, Fields.RELATION, "workingHours") && (
												<Tab label={Language("WORKING_HOURS")} value={RelationTabs.WorkingHours} />
											)}

											{hasOptionalFieldPermission(session, Fields.RELATION, "contacts") && showContactsTab({ isCustomer, isShipManager, isSkipper, isOperator, isBroker, isSurveyor }) && (
												<Tab
													label={Language("CONTACTS")}
													value={RelationTabs.Contacts}
													sx={{ color: hasErrorInContactsTab() ? (theme) => theme.palette.error.main : undefined }} />
											)}

											{isShipManager && (
												<Tab label={Language("SHIP_MANAGER_SHIPS")} value={RelationTabs.ShipManagerShips} />
											)}

											{isSkipper && (
												<Tab label={Language("SKIPPER_SHIPS")} value={RelationTabs.SkipperShips} />
											)}

											{isOperator && (
												<Tab label={Language("OPERATORS_SHIPS")} value={RelationTabs.OperatorShips} />
											)}

											{((hasPluginPermission(session, "customerLocations") && isCustomer) || (hasPluginPermission(session, "locationRelations") && isLocationManager)) && (
												<Tab label={Language("LOCATIONS")} value={RelationTabs.Locations} />
											)}

											{hasOptionalFieldPermission(session, Fields.RELATION, "products") && (isCustomer || isSupplier) && (
												<Tab label={Language("PRODUCTS")} value={RelationTabs.Products} />)}

											{isCustomer && (
												<Tab label={Language("LINKED_CUSTOMERS")} value={RelationTabs.LinkedCustomers} />
											)}
										</Tabs>
									</Box>

									<div style={{ padding: (selectedTab === RelationTabs.Contacts || selectedTab === RelationTabs.Locations) ? 0 : 16 }}>
										{selectedTab === RelationTabs.General && (
											<RelationGeneralForm />
										)}

										{selectedTab === RelationTabs.WorkingHours && (
											<React.Fragment>
												{Object.values(Day).map(day => (
													<FormGroup
														label={intl.formatMessage({ id: day.toUpperCase() })}
														labelPlacement="left-center"
														overflow="hidden"
														key={day}>
														<Grid spacing={3} container>
															<Grid size={{ xs: 12, sm: 6 }}>
																<Controller
																	name={`workingHours.${day}From`}
																	control={form.control}
																	render={({ field }) => (
																		<TimePicker
																			{...field}
																			placeholderText={intl.formatMessage({ id: "FROM" })}
																		/>
																	)}
																/>
															</Grid>

															<Grid size={{ xs: 12, sm: 6 }}>
																<Controller
																	name={`workingHours.${day}To`}
																	control={form.control}
																	render={({ field }) => (
																		<TimePicker
																			{...field}
																			placeholderText={intl.formatMessage({ id: "TO" })}
																		/>
																	)}
																/>
															</Grid>
														</Grid>
													</FormGroup>
												))}
											</React.Fragment>
										)}

										{selectedTab === RelationTabs.Contacts && (
											<RelationContactsForm />
										)}

										{selectedTab === RelationTabs.ShipManagerShips && (
											<RelationShipsForm
												identifier="managedShips"
												getOptionDisabled={id ? option => option.shipManager ? (option.shipManager.id !== +id) : false : undefined}
											/>
										)}

										{selectedTab === RelationTabs.SkipperShips && (
											<RelationShipsTable ships={skipperShip ? [ { ...skipperShip, id: skipperShip.id ?? 0, name: skipperShip.name ?? "" } ] : []} />
										)}

										{selectedTab === RelationTabs.OperatorShips && (
											<RelationShipsForm
												identifier="operatorShips"
												getOptionDisabled={id ? option => option.operator ? (option.operator.id !== +id) : false : undefined}
											/>
										)}

										{selectedTab === RelationTabs.Locations && (
											<RelationLocationsForm />
										)}

										{selectedTab === RelationTabs.Products && (
											<RelationProductsForm />
										)}

										{selectedTab === RelationTabs.LinkedCustomers && (
											<RelationLinkedCustomersForm />
										)}
									</div>
								</Card>

								<div style={{ marginTop: 16 }}>
									<Card title={Language("REMARKS")}>
										<Controller
											name="remarks"
											control={form.control}
											render={({ field }) => <Textarea {...field} rows={6} />} />
									</Card>
								</div>
							</Grid>
						</Grid>
					)}
				</form>
			</div>
		</FormProvider>
	);
};

export default RelationCreateEdit;
